import { formatDate, formatNumber } from '@angular/common';
import { Visit } from '@com-delta-idp/mnx-skymiles-overview-ui';
import { Image } from '@com-delta-omnichannel/aura';
import { SkyClubTrackerConstant as Constant, VisitRecord } from '../models';
import { VisitData, VisitSpend } from '../models/aem/visit-spend.interface';
import {
  DSCSpend,
  DscSpendTracker
} from '../models/api/tracker-details.interfaces';
import { getImageObj, isDiscontinuedDate } from './amex-skyclub-helper.utils';

export const formatVisitDescription = (
  template: string,
  date: string,
  format = Constant.DATE_FORMAT,
  locale = Constant.EN_US
): string => {
  return (
    template.replace(
      Constant.DATE,
      date !== '' ? formatDate(date, format, locale) : ''
    ) || ''
  );
};

export const getUnlimitedVisitData = (
  aem: VisitSpend,
  unlimitedDSCDiscontinueDate: string | undefined,
  imageType: string
): VisitData => ({
  title: aem.unlimited.title,
  description: formatVisitDescription(
    aem.unlimited.congratsInfo,
    unlimitedDSCDiscontinueDate || ''
  ),
  count: {},
  imageInfo: getImageObj(aem.cardImages, imageType)
});

export const getPreviousVisitData = (
  aem: VisitSpend,
  unlimitedDSCDiscontinueDate: string | undefined
): VisitData => ({
  title: aem.unlimited.title,
  description: formatVisitDescription(
    aem.unlimited.Validity,
    unlimitedDSCDiscontinueDate || ''
  ),
  count: {},
  imageInfo: getImageObj(aem.cardImages, Constant.UNLIMITED_ACCESS)
});

export const getZeroVisitsData = (
  aem: VisitSpend,
  currentObj: DSCSpend,
  record: VisitRecord
): VisitData => ({
  title: aem.zeroVisitLabel,
  description: aem.zeroVisitDescription.replace(
    Constant.REMAIN,
    `${Constant.USD}${formatNumber(
      currentObj.remainderAmt,
      Constant.EN_US,
      '1.0-0'
    )}`
  ),
  count: { count: record.annualVisitAllotment - record.visitsUsed },
  imageInfo: getImageObj(aem.cardImages, Constant.ZERO_VISITS)
});

export const getVisit = (
  aem: VisitSpend,
  record: VisitRecord,
  trackerContent: DscSpendTracker
): Visit => {
  const currentObj = getObject(trackerContent.DSCSpend, Constant.CURRENT);
  const previousObj = getObject(trackerContent.DSCSpend, Constant.PREVIOUS);
  let visitData: VisitData = {
    title: '',
    description: '',
    count: {},
    imageInfo: {} as Image
  };

  if (currentObj?.['thresholdMet']) {
    visitData = getUnlimitedVisitData(
      aem,
      trackerContent.unlimitedDSCDiscontinueDate,
      Constant.UNLIMITED_EVER_GREEN
    );
  } else if (
    checkUnlimitedAccess(
      trackerContent.unlimitedDSCDiscontinueDate,
      currentObj?.['thresholdMet'],
      previousObj?.['thresholdMet']
    )
  ) {
    visitData = getPreviousVisitData(
      aem,
      trackerContent.unlimitedDSCDiscontinueDate
    );
  } else if (record.visitsUsed === record.annualVisitAllotment) {
    visitData = getZeroVisitsData(aem, currentObj, record);
  } else {
    visitData = {
      title: aem.visitsAvailable,
      description: '',
      count: { count: record.annualVisitAllotment - record.visitsUsed },
      imageInfo: getImageObj(aem.cardImages, Constant.DEFAULT)
    };
  }

  return {
    ...visitData.count,
    maxCount: record.annualVisitAllotment,
    title: visitData.title,
    description: visitData.description,
    image: visitData.imageInfo
  };
};

export const getObject = (
  trackerInfo: DSCSpend[],
  status: string
): DSCSpend => {
  return (
    trackerInfo.find((spend) => spend.statusDesc === status) ?? ({} as DSCSpend)
  );
};

export const checkUnlimitedAccess = (
  discontinueDate: string | undefined,
  currentThreshold: boolean,
  previousThreshold: boolean
): boolean => {
  return (
    previousThreshold ||
    (isDiscontinuedDate(discontinueDate) &&
      !currentThreshold &&
      !previousThreshold)
  );
};
